import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiFilter } from 'react-icons/fi';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../../components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { getProducts, deleteProduct } from '../../../services/productService';
import { getCategories } from '../../../services/categoryService';
import { getTags } from '../../../services/tagService';
import VeeqoSyncStatus from '../../../components/admin/VeeqoSyncStatus';

const ProductListPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    tag: '',
    minPrice: '',
    maxPrice: '',
    isPublished: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Fetch products with filters
  const { page, limit } = pagination;
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const filterParams = { ...filters, page, limit };
      const response = await getProducts(filterParams);

      setProducts(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total,
        totalPages: response.totalPages
      }));
      setError(null);
    } catch (err) {
      setError(err.toString());
      console.error('Error fetching products:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, limit]);

  // Fetch categories and tags for filters
  const fetchCategoriesAndTags = useCallback(async () => {
    try {
      const [categoriesResponse, tagsResponse] = await Promise.all([
        getCategories(),
        getTags()
      ]);

      setCategories(categoriesResponse.data || []);
      setTags(tagsResponse.data || []);
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategoriesAndTags();
  }, [fetchCategoriesAndTags]);

  // Handle product deletion
  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await deleteProduct(productToDelete._id);
      setProducts(products.filter(product => product._id !== productToDelete._id));
      setDeleteModalOpen(false);
      setProductToDelete(null);
    } catch (err) {
      setError(err.toString());
      console.error('Error deleting product:', err);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (product, e) => {
    e.stopPropagation(); // Prevent row click
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setPagination({ ...pagination, page: 1 }); // Reset to first page
    fetchProducts();
    setFilterModalOpen(false);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      category: '',
      tag: '',
      minPrice: '',
      maxPrice: '',
      isPublished: ''
    });
    setPagination({ ...pagination, page: 1 });
    fetchProducts();
    setFilterModalOpen(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  return (
    <div className="p-6">
      <VeeqoSyncStatus type="products" />
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Products</CardTitle>
            <div className="flex space-x-2">
              <Dialog open={filterModalOpen} onOpenChange={setFilterModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <FiFilter className="mr-2 h-4 w-4" /> Filters
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Filter Products</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="search">Search</Label>
                      <Input id="search" placeholder="Search by title or SKU" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select onValueChange={(value) => setFilters({ ...filters, category: value })} value={filters.category}>
                        <SelectTrigger>
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Categories</SelectItem>
                          {categories.map((category) => (
                            <SelectItem key={category._id} value={category._id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tag</Label>
                      <Select onValueChange={(value) => setFilters({ ...filters, tag: value })} value={filters.tag}>
                        <SelectTrigger>
                          <SelectValue placeholder="All Tags" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Tags</SelectItem>
                          {tags.map((tag) => (
                            <SelectItem key={tag._id} value={tag._id}>
                              {tag.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="minPrice">Min Price (£)</Label>
                        <Input id="minPrice" type="number" placeholder="0.00" value={filters.minPrice} onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maxPrice">Max Price (£)</Label>
                        <Input id="maxPrice" type="number" placeholder="0.00" value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select onValueChange={(value) => setFilters({ ...filters, isPublished: value })} value={filters.isPublished}>
                        <SelectTrigger>
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All</SelectItem>
                          <SelectItem value="true">Published</SelectItem>
                          <SelectItem value="false">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={handleResetFilters}>Reset</Button>
                    <Button onClick={handleApplyFilters}>Apply Filters</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button onClick={() => navigate('/admin/products/add')}>
                <FiPlus className="mr-2 h-4 w-4" /> Add Product
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              Error: {error}
            </div>
          )}
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Veeqo ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Size Chart</TableHead>
                  <TableHead>QR Code</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product._id} onClick={() => navigate(`/admin/products/edit/${product._id}`)} className="cursor-pointer">
                    <TableCell>
                      <img
                        src={product.images?.find(img => img.isMain)?.url || product.images?.[0]?.url || '/images/placeholder.jpg'}
                        alt={product.title}
                        className="w-10 h-10 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {product.veeqoProductId || 'Not Synced'}
                    </TableCell>
                    <TableCell className="font-medium">{product.title}</TableCell>
                    <TableCell>£{product.price.toFixed(2)}</TableCell>
                    <TableCell>{product.category?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {product.sizeChart && typeof product.sizeChart === 'object' ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      {product.qrCodeUrl ? (
                        <div className="flex items-center space-x-2">
                          <img
                            src={product.qrCodeUrl}
                            alt="QR Code"
                            className="w-12 h-12 object-contain border border-gray-300 rounded"
                            title="Product QR Code"
                          />
                          <a
                            href={product.qrCodeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View
                          </a>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">Not generated</span>
                      )}
                    </TableCell>
                    <TableCell>{product.totalStock || 0}</TableCell>
                    <TableCell>
                      <Badge variant={product.isPublished ? 'success' : 'secondary'}>
                        {product.isPublished ? 'Published' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/admin/products/view/${product._id}`); }}>
                          <FiEye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/admin/products/edit/${product._id}`); }}>
                          <FiEdit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => openDeleteModal(product, e)}>
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        <CardFooter>
          {pagination.totalPages > 1 && (
            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            Showing {products.length} of {pagination.total} products
          </div>
          <div className="flex space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            {[...Array(pagination.totalPages)].map((_, index) => (
              <Button
                key={index + 1}
                variant={pagination.page === index + 1 ? "primary" : "outline"}
                size="sm"
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
          </DialogHeader>
          <p>
            Are you sure you want to delete{' '}
            <strong>{productToDelete?.title}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteProduct}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductListPage;
